import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  EyeIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  BuildingOfficeIcon,
  ClockIcon,
  CurrencyDollarIcon,
  EllipsisVerticalIcon,
  BellIcon,
  ChartBarIcon,
  TrendingUpIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid,
  ClockIcon as ClockIconSolid,
  TruckIcon,
  CreditCardIcon,
  XCircleIcon as XCircleIconSolid,
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
  PauseIcon,
  CheckIcon
} from '@heroicons/react/24/solid';

// Custom components
import OrderDetailsModal from './OrderDetailsModal';

// Custom hook
import useLiveOrders from './useLiveOrders';

// Utils
import { 
  formatCurrency, 
  formatDate, 
  orderNeedsAttention,
  debounce
} from './liveOrdersUtils';

// Status icon mapping
const getStatusIcon = (status) => {
  const statusIcons = {
    pending: ClockIconSolid,
    confirmed: CheckCircleIconSolid,
    quotation_sent: CreditCardIcon,
    awaiting_payment: CreditCardIcon,
    processing: ExclamationTriangleIconSolid,
    shipped: TruckIcon,
    delivered: CheckIcon,
    completed: CheckCircleIconSolid,
    on_hold: PauseIcon,
    cancelled: XCircleIconSolid
  };
  return statusIcons[status] || ClockIconSolid;
};

const LiveOrdersDashboard = () => {
  // Custom hook for orders management
  const {
    orders,
    filteredOrders,
    metrics,
    loading,
    error,
    isConnected,
    filters,
    viewPreferences,
    updateFilters,
    updateViewPreferences,
    refresh,
    search,
    setStatusFilter,
    exportOrders,
    getOrdersByTab,
    updateOrderStatus,
    sendEmail
  } = useLiveOrders();

  // Local state
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination
  const itemsPerPage = viewPreferences.itemsPerPage || 25;
  const currentOrders = getOrdersByTab(activeTab);
  const totalPages = Math.ceil(currentOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOrders = currentOrders.slice(startIndex, endIndex);

  // Event handlers
  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setOrderDetailsOpen(true);
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    const result = await updateOrderStatus(orderId, newStatus);
    if (result.success) {
      setSnackbar({
        open: true,
        message: 'Order status updated successfully',
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to update order status',
        severity: 'error'
      });
    }
  };

  const handleSendEmail = async (orderId, emailData) => {
    const result = await sendEmail(orderId, emailData);
    if (result.success) {
      setSnackbar({
        open: true,
        message: 'Email sent successfully',
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to send email',
        severity: 'error'
      });
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    search(value);
    setCurrentPage(1);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setCurrentPage(1);
    setSelectedOrders([]);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleExport = () => {
    const result = exportOrders('csv');
    if (result.success) {
      setSnackbar({
        open: true,
        message: 'Orders exported successfully',
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to export orders',
        severity: 'error'
      });
    }
  };

  const handleRefresh = () => {
    refresh();
    setSnackbar({
      open: true,
      message: 'Orders refreshed',
      severity: 'info'
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedOrders(paginatedOrders.map(order => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSort = (column) => {
    const isAsc = sortBy === column && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(column);
    updateFilters({ sortBy: column, sortOrder: isAsc ? 'desc' : 'asc' });
  };

// Status chip component - Fixed to use Tailwind classes
const StatusChip = ({ status, label }) => {
  const statusColors = {
    pending: 'bg-amber-500',
    confirmed: 'bg-blue-500',
    quotation_sent: 'bg-purple-500',
    awaiting_payment: 'bg-orange-500',
    processing: 'bg-indigo-500',
    shipped: 'bg-cyan-500',
    delivered: 'bg-green-500',
    completed: 'bg-emerald-500',
    on_hold: 'bg-gray-500',
    cancelled: 'bg-red-500'
  };
  
  const colorClass = statusColors[status] || 'bg-gray-500';
  
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold text-white ${colorClass}`}>
      {label}
    </span>
  );
};

// Priority chip component - Fixed to use Tailwind classes
const PriorityChip = ({ priority, label }) => {
  const priorityColors = {
    low: 'bg-green-500',
    medium: 'bg-amber-500',
    high: 'bg-red-500',
    urgent: 'bg-pink-500'
  };
  
  const colorClass = priorityColors[priority] || 'bg-gray-500';
  
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold text-white ${colorClass}`}>
      {label}
    </span>
  );
};

  // Connection indicator
  const ConnectionIndicator = () => (
    <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
      isConnected 
        ? 'bg-green-100 text-green-800' 
        : 'bg-red-100 text-red-800'
    }`}>
      <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
      {isConnected ? 'Connected' : 'Disconnected'}
    </div>
  );

  // Table columns configuration - Updated with better width management
  const columns = [
   /*  { id: 'select', label: '', sortable: false, width: 'w-12', minWidth: '48px' }, */
    { id: 'id', label: 'Order ID', sortable: true, width: 'w-28', minWidth: '112px' },
     { id: 'image', label: 'Image', sortable: false, width: 'w-20', minWidth: '80px' },
    { id: 'customer', label: 'Customer', sortable: true, width: 'w-56', minWidth: '224px' },
    { id: 'supplier', label: 'Supplier', sortable: true, width: 'w-56', minWidth: '224px' },
    { id: 'status', label: 'Status', sortable: true, width: 'w-32', minWidth: '128px' },
/*     { id: 'priority', label: 'Priority', sortable: true, width: 'w-24', minWidth: '96px' },
 */    { id: 'amount', label: 'Amount', sortable: true, width: 'w-32', minWidth: '128px' },
    { id: 'date', label: 'Order Date', sortable: true, width: 'w-32', minWidth: '128px' },
    { id: 'lastInteraction', label: 'Last Update', sortable: true, width: 'w-40', minWidth: '160px' },
/*     { id: 'actions', label: 'Actions', sortable: false, width: 'w-32', minWidth: '128px' }
 */  ];

// Render table header - Updated with proper styling
  const renderTableHeader = () => (
    <thead className="bg-gray-50 sticky top-0 z-10">
      <tr>
        {columns.map((column) => (
          <th
            key={column.id}
            className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.width}`}
            style={{ minWidth: column.minWidth }}
          >
            {column.id === 'select' ? (
              <input
                type="checkbox"
                checked={paginatedOrders.length > 0 && selectedOrders.length === paginatedOrders.length}
                onChange={handleSelectAll}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
            ) : column.sortable ? (
              <button
                onClick={() => handleSort(column.id)}
                className="flex items-center gap-1 hover:text-gray-700 whitespace-nowrap"
              >
                {column.label}
                {sortBy === column.id && (
                  <span className="text-blue-500">
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </button>
            ) : (
              <span className="whitespace-nowrap">{column.label}</span>
            )}
          </th>
        ))}
      </tr>
    </thead>
  );

  // Render table row - Updated with proper styling
  const renderTableRow = (order) => {
    const attention = orderNeedsAttention(order);
    const isSelected = selectedOrders.includes(order.id);
    
    return (
      <tr 
        key={order.id}
        onClick={() => handleOrderClick(order)}
        className={`cursor-pointer transition-colors hover:bg-gray-50 ${
          isSelected ? 'bg-blue-50' : ''
        }`}
      >
{/*         <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleSelectOrder(order.id)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
        </td> */}
        
        <td className="px-4 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-900">
              #{order.id}
            </span>
            {attention && (
              <ExclamationTriangleIcon 
                className="ml-2 w-4 h-4 text-yellow-500 flex-shrink-0" 
                title={attention.reason}
              />
            )}
          </div>
        </td>

      <td className="px-4 py-4 whitespace-nowrap">
  <div className="flex items-center justify-center">
    {order.image ? (
      <img 
        src={process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES + order.image} 
        alt="Order" 
        className="w-12 h-8 object-cover rounded border border-gray-200 cursor-pointer"
        onClick={() => navigate('/admin/app/materialreq2', { state: { quotationId:order.quotationId } })} 
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
    ) : null}

    <div 
      className={`w-12 h-8 bg-gray-100 border border-gray-200 rounded flex items-center justify-center flex-col text-center ${order.image ? 'hidden' : 'flex'}`}
    >
      <span className="text-[10px] text-gray-500 leading-tight">No</span>
      <span className="text-[10px] text-gray-500 leading-tight">Image</span>
    </div>
  </div>
</td>

        
        <td className="px-4 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className="flex-shrink-0 w-8 h-8">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <UserIcon className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 truncate">
                {order.customerName}
              </div>
              <div className="text-sm text-gray-500 truncate">
                {order.customerPhone}
              </div>
            </div>
          </div>
        </td>
        
        <td className="px-4 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className="flex-shrink-0 w-8 h-8">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                <BuildingOfficeIcon className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="ml-3 min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 truncate">
                {order.supplierName}
              </div>
              <div className="text-sm text-gray-500 truncate">
                {order.supplierPhone}
              </div>
            </div>
          </div>
        </td>
        
        <td className="px-4 py-4 whitespace-nowrap">
          <StatusChip
            status={order.status}
            label={order.status?.replace('_', ' ').toUpperCase()}
          />
        </td>
        
{/*         <td className="px-4 py-4 whitespace-nowrap">
          <PriorityChip
            priority={order.priority}
            label={order.priority?.toUpperCase()}
          />
        </td> */}
        
        <td className="px-4 py-4 whitespace-nowrap">
          <div>
            <div className="text-sm font-medium text-blue-600">
              {formatCurrency(order.total)}
            </div>
            <div className="text-sm text-gray-500">
              {order.items?.length || 0} items
            </div>
          </div>
        </td>
        
        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
          <div className="text-sm text-gray-900">
            {formatDate(order.orderDate)}
          </div>
        </td>
        
        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
          <div className="text-sm text-gray-900">
            {formatDate(order.lastInteraction, 'datetime')}
          </div>
        </td>
        
{/*         <td className="px-4 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleOrderClick(order)}
              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
              title="View Details"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setSelectedOrder(order);
                setOrderDetailsOpen(true);
              }}
              className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
              title="Send Email"
            >
              <EnvelopeIcon className="w-4 h-4" />
            </button>
            <button
              className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-50"
              title="More Actions"
            >
              <EllipsisVerticalIcon className="w-4 h-4" />
            </button>
          </div>
        </td> */}
      </tr>
    );
  };

  // Render filters
  const renderFilters = () => (
    <div className="flex flex-wrap items-center gap-4 mb-4">
      <div className="flex-1 min-w-80">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-10 w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
      
      <select
        value={filters.statusFilter}
        onChange={(e) => {
          setStatusFilter(e.target.value);
          setCurrentPage(1);
        }}
        className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="all">All Status</option>
        <option value="response pending">Response Pending</option>
        <option value="payment pending">Payment Pending</option>
        <option value="quotation_sent">Quotation Sent</option>
        <option value="awaiting_payment">Awaiting Payment</option>
        <option value="processing">Processing</option>
        <option value="shipped">Shipped</option>
        <option value="delivered">Delivered</option>
        <option value="completed">Completed</option>
        <option value="on_hold">On Hold</option>
        <option value="cancelled">Cancelled</option>
      </select>

{/*       <select
        value={filters.priorityFilter}
        onChange={(e) => {
          updateFilters({ priorityFilter: e.target.value });
          setCurrentPage(1);
        }}
        className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="all">All Priority</option>
        <option value="low">Low</option>
        <option value="medium">Medium</option>
        <option value="high">High</option>
        <option value="urgent">Urgent</option>
      </select> */}

      <select
        value={viewPreferences.itemsPerPage || 25}
        onChange={(e) => {
          updateViewPreferences({ itemsPerPage: parseInt(e.target.value) });
          setCurrentPage(1);
        }}
        className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value={10}>10 per page</option>
        <option value={25}>25 per page</option>
        <option value={50}>50 per page</option>
        <option value={100}>100 per page</option>
      </select>

      <div className="flex items-center gap-2">
{/*         <button
          onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          title="More Filters"
        >
          <FunnelIcon className="w-5 h-5" />
        </button> */}

        <button
          onClick={handleRefresh}
          disabled={loading}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          title="Refresh"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          ) : (
            <ArrowPathIcon className="w-5 h-5" />
          )}
        </button>

{/*         <button
          onClick={handleExport}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          title="Export"
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
        </button> */}

        <button
          onClick={() => setSettingsOpen(true)}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          title="Settings"
        >
          <Cog6ToothIcon className="w-5 h-5" />
        </button>
      </div>

      <ConnectionIndicator />
    </div>
  );

  // Render bulk actions
  const renderBulkActions = () => (
    selectedOrders.length > 0 && (
      <div className="flex items-center justify-between gap-4 mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <span className="text-sm font-medium text-blue-900">
          {selectedOrders.length} order(s) selected
        </span>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1 text-sm bg-white border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 transition-colors">
            <EnvelopeIcon className="w-4 h-4 inline mr-1" />
            Send Email
          </button>
          <button className="px-3 py-1 text-sm bg-white border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 transition-colors">
            <PencilIcon className="w-4 h-4 inline mr-1" />
            Update Status
          </button>
          <button className="px-3 py-1 text-sm bg-white border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 transition-colors">
            <ArrowDownTrayIcon className="w-4 h-4 inline mr-1" />
            Export Selected
          </button>
          <button 
            onClick={() => setSelectedOrders([])}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Clear Selection
          </button>
        </div>
      </div>
    )
  );

  // Render settings dialog
  const renderSettingsDialog = () => (
    settingsOpen && (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div 
            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
            onClick={() => setSettingsOpen(false)} 
          />
          
          <div className="inline-block w-full max-w-md p-6 my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Dashboard Settings</h3>
              <button
                onClick={() => setSettingsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Auto Refresh</label>
                <button
                  onClick={() => updateViewPreferences({ autoRefresh: !viewPreferences.autoRefresh })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    viewPreferences.autoRefresh ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    viewPreferences.autoRefresh ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Interval</label>
                <select
                  value={viewPreferences.refreshInterval}
                  onChange={(e) => updateViewPreferences({ refreshInterval: parseInt(e.target.value) })}
                  disabled={!viewPreferences.autoRefresh}
                  className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value={10000}>10 seconds</option>
                  <option value={30000}>30 seconds</option>
                  <option value={60000}>1 minute</option>
                  <option value={300000}>5 minutes</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Items Per Page</label>
                <select
                  value={viewPreferences.itemsPerPage}
                  onChange={(e) => updateViewPreferences({ itemsPerPage: parseInt(e.target.value) })}
                  className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

{/*               <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Show Metrics Cards</label>
                <button
                  onClick={() => updateViewPreferences({ showMetrics: !viewPreferences.showMetrics })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    viewPreferences.showMetrics ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    viewPreferences.showMetrics ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div> */}

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Compact Table View</label>
                <button
                  onClick={() => updateViewPreferences({ compactView: !viewPreferences.compactView })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    viewPreferences.compactView ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    viewPreferences.compactView ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSettingsOpen(false)}
                className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );

  // Render tabs
  const renderTabs = () => {
    const tabCounts = {
      all: filteredOrders.length,
      pending: filteredOrders.filter(o => ['pending', 'confirmed'].includes(o.status)).length,
      active: filteredOrders.filter(o => ['processing', 'shipped'].includes(o.status)).length,
      completed: filteredOrders.filter(o => ['delivered', 'completed'].includes(o.status)).length,
      attention: filteredOrders.filter(o => orderNeedsAttention(o)).length
    };

    const tabs = [
      { label: 'All Orders', count: tabCounts.all, color: 'blue' },
      { label: 'Pending', count: tabCounts.pending, color: 'yellow' },
      { label: 'Active', count: tabCounts.active, color: 'blue' },
      { label: 'Completed', count: tabCounts.completed, color: 'green' },
/*       { label: 'Needs Attention', count: tabCounts.attention, color: 'red' }
 */    ];

    return (
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab, index) => (
            <button
              key={index}
              onClick={() => handleTabChange(null, index)}
              className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === index
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full ${
                  tab.color === 'blue' ? 'text-blue-800  bg-blue-100' :
                  tab.color === 'yellow' ? 'text-yellow-800 bg-yellow-100' :
                  tab.color === 'green' ? 'text-green-800 bg-green-100' :
                  tab.color === 'red' ? 'text-red-800 bg-red-100' : 'text-gray-800 bg-gray-100'
                }`}>
                  {tab.count > 999 ? '999+' : tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>
    );
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-gray-700">
          Showing {startIndex + 1}-{Math.min(endIndex, currentOrders.length)} of {currentOrders.length} orders
        </div>
        
        <nav className="flex items-center gap-1">
          <button
            onClick={() => handlePageChange(null, 1)}
            disabled={currentPage === 1}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              currentPage === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            First
          </button>
          
          <button
            onClick={() => handlePageChange(null, currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              currentPage === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Previous
          </button>
          
          {pages.map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(null, page)}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                currentPage === page
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(null, currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              currentPage === totalPages
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Next
          </button>
          
          <button
            onClick={() => handlePageChange(null, totalPages)}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              currentPage === totalPages
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Last
          </button>
        </nav>
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen px-2 sm:px-6 lg:px-8 py-4 pb-8">
      {/* Header */}
      <div className="mb-4 border-b border-gray-200 pb-2">
        <h1 className="text-xl font-semibold text-gray-900">Live Orders Dashboard</h1>
        <p className="text-sm text-gray-600">Monitor and manage orders in real-time</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircleIcon className="w-5 h-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Cards */}
{/*       {viewPreferences.showMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Orders</dt>
                    <dd className="text-lg font-medium text-gray-900">{orders.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Active Orders</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {orders.filter(o => !['completed', 'cancelled', 'delivered'].includes(o.status)).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CurrencyDollarIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(orders.reduce((sum, order) => sum + (order.total || 0), 0))}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Needs Attention</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {orders.filter(o => orderNeedsAttention(o)).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* Filters */}
      {renderFilters()}

      {/* Tabs */}
      {renderTabs()}

      {/* Bulk Actions */}
      {renderBulkActions()}

      {/* Orders Table - Updated with full width */}
      {loading && paginatedOrders.length === 0 ? (
        <div className="flex justify-center items-center min-h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#f7525A] border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading orders...</p>
          </div>
        </div>
      ) : paginatedOrders.length === 0 ? (
        <div className="text-center py-12">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Orders Found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filters.statusFilter !== 'all' || filters.priorityFilter !== 'all'
              ? 'Try adjusting your filters to see more results.'
              : 'Orders will appear here when they are received.'}
          </p>
          {(searchTerm || filters.statusFilter !== 'all' || filters.priorityFilter !== 'all') && (
            <div className="mt-6">
              <button
                onClick={() => {
                  setSearchTerm('');
                  updateFilters({ statusFilter: 'all', priorityFilter: 'all' });
                  search('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700  bg-white hover:bg-gray-50 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Table Container with Full Width */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg w-full mb-10">
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-300px)] w-full">
              <div className="w-full inline-block align-middle">
                <table className="w-full divide-y divide-gray-200" style={{ minWidth: '1400px' }}>
                  {renderTableHeader()}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedOrders.map(renderTableRow)}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Table Footer with Row Count */}
            <div className="bg-gray-50 px-6 py-2 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {startIndex + 1} to {Math.min(endIndex, currentOrders.length)} of {currentOrders.length} results
                </div>
                <div className="text-sm text-gray-500">
                  {selectedOrders.length > 0 && `${selectedOrders.length} selected`}
                </div>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {renderPagination()}
        </>
      )}

      {/* Order Details Modal */}
      <OrderDetailsModal
        open={orderDetailsOpen}
        onClose={() => {
          setOrderDetailsOpen(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
        onStatusUpdate={handleStatusUpdate}
        onSendEmail={handleSendEmail}
        onUpdateOrder={(updatedOrder) => {
          // Handle order update
          console.log('Order updated:', updatedOrder);
        }}
      />

      {/* Settings Dialog */}
      {renderSettingsDialog()}

      {/* Filter Menu - Updated positioning for full width */}
      {filterMenuAnchor && (
        <div className="fixed inset-0 z-40" onClick={() => setFilterMenuAnchor(null)}>
          <div 
            className="absolute bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80 max-h-96 overflow-y-auto"
            style={{
              top: '120px',
              right: '20px',
              maxWidth: 'calc(100vw - 40px)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium text-gray-900 mb-4">Advanced Filters</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="From"
                  />
                  <input
                    type="date"
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="To"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Min"
                  />
                  <input
                    type="number"
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Max"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Type
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm">
                  <option value="">All Types</option>
                  <option value="new">New Customer</option>
                  <option value="returning">Returning Customer</option>
                  <option value="vip">VIP Customer</option>
                </select>
              </div>
              
              <hr className="border-gray-200" />
              
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    // Apply filters logic here
                    setFilterMenuAnchor(null);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Apply Filters
                </button>
                <button
                  onClick={() => {
                    // Reset all filters
                    setSearchTerm('');
                    updateFilters({ 
                      statusFilter: 'all', 
                      priorityFilter: 'all',
                      dateFrom: null,
                      dateTo: null,
                      amountFrom: null,
                      amountTo: null
                    });
                    search('');
                    setFilterMenuAnchor(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar for notifications */}
      {snackbar.open && (
        <div 
          className={`fixed bottom-4 right-4 z-50 max-w-sm w-full p-4 rounded-md shadow-lg transition-all duration-300 ${
            snackbar.severity === 'success' ? 'bg-green-50 border border-green-200' :
            snackbar.severity === 'error' ? 'bg-red-50 border border-red-200' :
            snackbar.severity === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
            'bg-blue-50 border border-blue-200'
          }`}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {snackbar.severity === 'success' && <CheckCircleIcon className="w-5 h-5 text-green-400" />}
              {snackbar.severity === 'error' && <XCircleIcon className="w-5 h-5 text-red-400" />}
              {snackbar.severity === 'warning' && <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400" />}
              {snackbar.severity === 'info' && <InformationCircleIcon className="w-5 h-5 text-blue-400" />}
            </div>
            <div className="ml-3 flex-1">
              <p className={`text-sm font-medium ${
                snackbar.severity === 'success' ? 'text-green-800' :
                snackbar.severity === 'error' ? 'text-red-800' :
                snackbar.severity === 'warning' ? 'text-yellow-800' :
                'text-blue-800'
              }`}>
                {snackbar.message}
              </p>
            </div>
            <button
              onClick={() => setSnackbar({ ...snackbar, open: false })}
              className="ml-4 inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Floating Action Button for Quick Actions */}
{/*       <div className="fixed bottom-6 right-6 z-40">
        <div className="relative">
          {orders.filter(o => orderNeedsAttention(o)).length > 0 && (
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold z-10">
              {orders.filter(o => orderNeedsAttention(o)).length > 9 ? '9+' : orders.filter(o => orderNeedsAttention(o)).length}
            </div>
          )}
          
          <button
            onClick={() => {
              // Toggle notifications panel or quick actions
              console.log('Quick actions clicked');
            }}
            className="w-14 h-14 bg-[#f7525A] hover:bg-[#f7525A] text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f7525A]-500 hover:scale-105"
            title="Quick Actions"
          >
            <BellIcon className="w-6 h-6" />
          </button>
        </div>
      </div> */}

      {/* Mobile Table Notice */}
      <div className="block sm:hidden bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
        <div className="flex">
          <InformationCircleIcon className="w-5 h-5 text-blue-400 flex-shrink-0" />
          <div className="ml-3">
            <p className="text-sm text-blue-800">
              For the best experience viewing the orders table, please use a larger screen or rotate your device to landscape mode.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveOrdersDashboard;


